import { useMemo } from 'react';

import { useCountriesAndCurrencies } from './useCountriesAndCurrencies';

export type CurrencyOption = Readonly<{
  code: string;
  name: string;
}>;

export const USD_CURRENCY_CODE = 'USD';

/** Not an ISO 4217 code, so absent from the gateway `Currency` enum despite the API accepting it. */
export const GBX_CURRENCY_OPTION: CurrencyOption = { code: 'GBX', name: 'Pence Sterling' };

/**
 * Mirrors BE `XcGlobalMarketOptions.SupportedCurrencyCodeList`. The gateway returns all 171 ISO
 * codes, most of which the API rejects, so this can't go until the BE exposes the supported set.
 */
export const SUPPORTED_CURRENCY_CODES: readonly string[] = [
  'AED',
  'ARS',
  'AUD',
  'BDT',
  'BHD',
  'BRL',
  'CAD',
  'CHF',
  'CLP',
  'CNY',
  'COP',
  'CZK',
  'DKK',
  'EGP',
  'EUR',
  'GBP',
  'GBX',
  'HKD',
  'HRK',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'ISK',
  'JOD',
  'JPY',
  'KES',
  'KRW',
  'KWD',
  'LKR',
  'MAD',
  'MXN',
  'MYR',
  'NGN',
  'NOK',
  'NZD',
  'OMR',
  'PEN',
  'PHP',
  'PKR',
  'PLN',
  'QAR',
  'RON',
  'RUB',
  'SAR',
  'SEK',
  'SGD',
  'SYP',
  'THB',
  'TRY',
  'TWD',
  'USD',
  'VND',
  'XOF',
  'ZAR',
];

/** Returns both code and name; callers format their own labels. */
export const useCurrencyOptions = () => {
  const { currencies, loading } = useCountriesAndCurrencies();

  const currencyOptions = useMemo<CurrencyOption[]>(() => {
    const supportedCurrencies = currencies
      .filter(({ id }) => SUPPORTED_CURRENCY_CODES.includes(id))
      .map<CurrencyOption>(({ id, displayName }) => ({ code: id, name: displayName }));

    // Guard on length so an in-flight/empty result doesn't look like a single-entry list, and
    // dedupe for whenever the gateway starts returning GBX itself.
    const withGbx =
      supportedCurrencies.length > 0 &&
      !supportedCurrencies.some(({ code }) => code === GBX_CURRENCY_OPTION.code)
        ? [...supportedCurrencies, GBX_CURRENCY_OPTION]
        : supportedCurrencies;

    return withGbx.sort((a, b) => {
      if (a.code === USD_CURRENCY_CODE) {
        return -1;
      }
      if (b.code === USD_CURRENCY_CODE) {
        return 1;
      }
      return a.code.localeCompare(b.code);
    });
  }, [currencies]);

  return { currencyOptions, loading };
};
